import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import ReactSelect from 'react-select';
import TextareaAutosize from 'react-textarea-autosize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { buildVendorCreatePayload, buildVendorUpdatePayload } from './vendorPayload';
const { rootUrl } = require('../../../config/config');

const PAYMENT_TERMS_OPTIONS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_45', label: 'Net 45' },
    { value: 'NET_60', label: 'Net 60' },
];

const inputCls = (error) =>
    `w-full px-3 py-2 border rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733] ${error ? 'border-red-400' : 'border-gray-300'}`;

const SectionTitle = ({ children }) => (
    <h2 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-4">{children}</h2>
);

const FieldLabel = ({ children, required }) => (
    <label className="block text-xs font-medium text-gray-600 mb-1">
        {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
);

const VendorFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');
    const isEditing = Boolean(id);

    const [formData, setFormData] = useState({
        code: '',
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        payment_terms: 'NET_30',
        tax_id: '',
        default_cashbook_account_id: '',
        is_preferred: false,
        is_active: true,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);

    useEffect(() => {
        fetchCashbookAccounts();
        if (isEditing) fetchVendor();
        else fetchNextCode();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchVendor = async () => {
        setFetching(true);
        try {
            const res = await axios.get(`${rootUrl}/parts/suppliers/${id}`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            const v = res.data;
            setFormData({
                code: v.code || '',
                name: v.name || '',
                contact_person: v.contact_person || '',
                phone: v.phone || '',
                email: v.email || '',
                address: v.address || '',
                payment_terms: v.payment_terms || 'NET_30',
                tax_id: v.tax_id || '',
                default_cashbook_account_id: v.default_cashbook_account_id || '',
                is_preferred: v.is_preferred || false,
                is_active: v.is_active !== false,
            });
        } catch (err) {
            console.error('Error fetching vendor:', err);
        } finally {
            setFetching(false);
        }
    };

    const fetchNextCode = async () => {
        try {
            const res = await axios.get(`${rootUrl}/parts/suppliers/next-code/preview`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.data?.code) setFormData(prev => ({ ...prev, code: res.data.code }));
        } catch (err) {
            console.error('Error fetching next code:', err);
        }
    };

    const fetchCashbookAccounts = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.data?.success) setCashbookAccounts(res.data.data || []);
        } catch (err) {
            console.error('Error fetching cashbook accounts:', err);
        }
    };

    const validate = () => {
        const errs = {};
        if (!formData.name.trim()) errs.name = 'Name is required';
        if (formData.phone && !/^\d{10}$/.test(formData.phone)) errs.phone = 'Phone must be 10 digits';
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) errs.email = 'Invalid email address';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            if (isEditing) {
                await axios.put(
                    `${rootUrl}/parts/suppliers/${id}`,
                    buildVendorUpdatePayload(formData, user?.username || ''),
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            } else {
                await axios.post(
                    `${rootUrl}/parts/suppliers`,
                    buildVendorCreatePayload(formData, user?.username || ''),
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            }
            navigate('/vendors');
        } catch (err) {
            setErrors({ submit: err.response?.data?.error || 'Error saving vendor' });
            setLoading(false);
        }
    };

    const cashbookOptions = [
        { value: '', label: 'None' },
        ...cashbookAccounts.map(a => ({ value: a.id, label: `${a.account_name} (${a.account_type})` }))
    ];
    const selectedCashbook = cashbookOptions.find(o => String(o.value) === String(formData.default_cashbook_account_id)) || cashbookOptions[0];
    const selectedPaymentTerms = PAYMENT_TERMS_OPTIONS.find(o => o.value === formData.payment_terms) || null;

    const selectStyles = {
        control: (base) => ({ ...base, minHeight: 34, borderRadius: 2, fontSize: 12, borderColor: '#d1d5db' }),
        option: (base) => ({ ...base, fontSize: 12 }),
    };

    if (fetching) {
        return <div className="p-6 text-xs text-gray-400">Loading...</div>;
    }

    return (
        <div className="p-4 md:p-6 min-h-screen bg-[#fff8f5]">
            {/* Page header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    type="button"
                    onClick={() => navigate('/vendors')}
                    className="h-8 w-8 flex items-center justify-center rounded-sm border border-gray-300 hover:bg-gray-50 text-gray-500 transition-colors flex-shrink-0"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                </button>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Vendors</p>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">
                        {isEditing ? 'Edit Vendor' : 'Add Vendor'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-3xl">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {/* Basic Information */}
                    <div className="p-5">
                        <SectionTitle>Basic Information</SectionTitle>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <FieldLabel>Code</FieldLabel>
                                <input
                                    value={formData.code}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-200 rounded-sm bg-gray-50 text-gray-400 text-xs cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <FieldLabel required>Name</FieldLabel>
                                <input
                                    value={formData.name}
                                    onChange={e => setFormData(p => ({ ...p, name: e.target.value.toUpperCase() }))}
                                    className={inputCls(errors.name)}
                                    placeholder="VENDOR NAME"
                                />
                                {errors.name && <p className="text-[10px] text-red-500 mt-1">{errors.name}</p>}
                            </div>
                            <div>
                                <FieldLabel>Tax ID</FieldLabel>
                                <input
                                    value={formData.tax_id}
                                    onChange={e => setFormData(p => ({ ...p, tax_id: e.target.value }))}
                                    className={inputCls(false)}
                                    placeholder="e.g. P051234567A"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Contact Information */}
                    <div className="p-5">
                        <SectionTitle>Contact Information</SectionTitle>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <FieldLabel>Contact Person</FieldLabel>
                                <input
                                    value={formData.contact_person}
                                    onChange={e => setFormData(p => ({ ...p, contact_person: e.target.value }))}
                                    className={inputCls(false)}
                                    placeholder="Full name"
                                />
                            </div>
                            <div>
                                <FieldLabel>Phone</FieldLabel>
                                <input
                                    value={formData.phone}
                                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                                    className={inputCls(errors.phone)}
                                    placeholder="0712345678"
                                />
                                {errors.phone && <p className="text-[10px] text-red-500 mt-1">{errors.phone}</p>}
                            </div>
                            <div>
                                <FieldLabel>Email</FieldLabel>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                                    className={inputCls(errors.email)}
                                    placeholder="vendor@example.com"
                                />
                                {errors.email && <p className="text-[10px] text-red-500 mt-1">{errors.email}</p>}
                            </div>
                            <div className="sm:col-span-2">
                                <FieldLabel>Address</FieldLabel>
                                <TextareaAutosize
                                    minRows={3}
                                    value={formData.address}
                                    onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733] resize-none"
                                    placeholder="Street, City, Country"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Financial Settings */}
                    <div className="p-5">
                        <SectionTitle>Financial Settings</SectionTitle>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <FieldLabel>Payment Terms</FieldLabel>
                                <ReactSelect
                                    options={PAYMENT_TERMS_OPTIONS}
                                    value={selectedPaymentTerms}
                                    onChange={opt => setFormData(p => ({ ...p, payment_terms: opt?.value || '' }))}
                                    isClearable={false}
                                    styles={selectStyles}
                                />
                            </div>
                            <div>
                                <FieldLabel>Default Cashbook Account</FieldLabel>
                                <ReactSelect
                                    options={cashbookOptions}
                                    value={selectedCashbook}
                                    onChange={opt => setFormData(p => ({ ...p, default_cashbook_account_id: opt?.value || '' }))}
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-5 mt-4 pt-1">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={formData.is_preferred}
                                    onChange={e => setFormData(p => ({ ...p, is_preferred: e.target.checked }))}
                                    className="w-4 h-4 accent-[#FF5733]"
                                />
                                <span className="text-xs text-gray-700">Preferred Supplier</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={e => setFormData(p => ({ ...p, is_active: e.target.checked }))}
                                    className="w-4 h-4 accent-[#FF5733]"
                                />
                                <span className="text-xs text-gray-700">Active</span>
                            </label>
                        </div>
                    </div>

                    {errors.submit && (
                        <div className="px-5 pb-3">
                            <p className="text-[10px] text-red-500">{errors.submit}</p>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="border-t border-gray-100 px-5 py-4 flex flex-col sm:flex-row justify-end gap-2 bg-gray-50/50">
                        <button
                            type="button"
                            onClick={() => navigate('/vendors')}
                            className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-100 text-xs font-medium rounded-sm transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#FF5733] hover:bg-[#ff4520] text-white text-xs font-semibold rounded-sm disabled:opacity-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faSave} />
                            {loading ? 'Saving...' : isEditing ? 'Update Vendor' : 'Save Vendor'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default VendorFormPage;
